import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { fetch3, fetch5, fetch6, fetch7 } from "../commonApis/commonApis";
import {
  addProfileData,
  setClientPermissions,
} from "../features/auth/authSlice";

const url = process.env.REACT_APP_BASE_URL;
interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

interface ApiPermission {
  module: string;
  permissions: string[];
}

export interface ClientPermission {
  module: string;
  operations: string[];
}
const normalise = (api: ApiPermission[]): ClientPermission[] =>
  api.map((item) => ({
    module: item.module,
    operations: item.permissions,
  }));

export const useClientPermissions = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state: any) => state.auth.profile);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["clientPermissions", profile?.id],
    queryFn: async () => {
      if (!profile?.id) throw new Error("Profile ID missing");

      const res = await fetch3(
        `${url}/api/access-center/${profile.id}/accesses`,
        "get"
      );

      if (!res?.success) {
        throw new Error(res?.message ?? "Failed to load permissions");
      }

      return res.data as ApiPermission[];
    },
    enabled: !!profile?.id,
    staleTime: 5 * 60 * 1000,
    select: normalise,
  });

  // Sync to Redux (replaces onSuccess)
  useEffect(() => {
    if (query.data) {
      dispatch(setClientPermissions(query.data));
    }
  }, [query.data, dispatch]);

  return query;
};

// /api/roles/:id delete role

export const useDeleteClientPermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (permissionId: string) => {
      const res: ApiResponse = await fetch7(
        `${url}/api/roles/${permissionId}`,
        "DELETE",
        null
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to delete permission");
      }

      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clientPermissions"],
      });
    },
  });
};


export const useInvalidateClientPermissions = () => {
  const queryClient = useQueryClient();
  const profile = useSelector((state: any) => state.auth.profile);

  return () => {
    queryClient.invalidateQueries({
      queryKey: ["clientPermissions", profile?.id],
    });
  };
};

export const useProfileData = () => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["getMe"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/auth/getMe`, "get");
      if (!res || !res.data) {
        return res.message;
      }
      if (res.data.token && typeof window !== "undefined") {
        window.localStorage.setItem("auth", res.data.token);
      }
      dispatch(addProfileData(res.data));
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useClientProfileData = () => {
  return useQuery({
    queryKey: ["getClientData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/client/profile`, "get");
      if (!res || !res.data) {
        return res.message;
      }
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useFetchAccessCenter = () => {
  return useQuery({
    queryKey: ["getAccessCenter"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/access-center`, "get");
      console.log(res?.data,'d')
      if (!res || !res.data) {
        return res.message;
      }
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  })
}

export const useClientConfigurationData = () => {
  return useQuery({
    queryKey: ["getClientConfigurationData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/client/configuration`, "get");
      if (!res || !res.data) {
        return res.message;
      }
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

interface UpdateProfilePayload {
  companyName: string;
  contactPerson: string;
  companyCrNo: string;
  companyVatNo: string;
  systemMail: string;
  country: string;
  email: string;
  phoneNumber: string;
  state: string;
  city: string;
  zipCode: string;
  clientAddress: string;
  lmAwbNumber: string;
  shortAddress: string;
  tollFreeNo: string;
  crDoc: File;
  vatDoc: File;
}

export const useUpdateProfile = () => {
  return useMutation({
    mutationFn: async (payload: UpdateProfilePayload) => {
      const res: ApiResponse = await fetch7(
        `${url}/api/client/update/profile`,
        "PUT",
        payload
      );

      if (!res.success) {
        throw res.message || res?.error || "Failed to update profile";
      }

      return res;
    },
    onSuccess: (data) => {
      console.log("Profile updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("Profile update failed:", error.message);
      return error;
    },
  });
};

export const useUpdateProfilePicMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch6(
        `${url}/api/client/update/logo`,
        payload
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to update logo");
      }

      return res.data;
    },
    onSuccess: (data) => {
      console.log("Logo updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("Logo update failed:", error.message);
    },
  });
};

export const useUpdateConfigurationMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/client/update/configuration`,
        "PUT",
        payload
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to update configuration");
      }

      return res.data;
    },
    onSuccess: (data) => {
      console.log("Configuration updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("Configuration update failed:", error.message);
    },
  });
};
